import { useState, useEffect, useContext, useRef, useCallback } from "react";
import GroupDialog from "./GroupDialog";
import { getAllData } from "../../../../utils/helpers/fetching";
import { connect } from "react-redux";
import { compose } from "redux";
import { withApollo } from "react-apollo";
import {
  LIST_BOOKING_GROUP_CONFIGURATION_BY_CREATE_DATE,
  LIST_DIGITAL_LAB_BOOKING_USERS,
  LIST_BOOKING_GROUP_CONFIGURATION_UNICITY_CHECK,
  LIST_BOOKING_ACCESS_CONFIGURATION_BY_EQUIPMENT,
  LIST_BOOKING_ACCESS_CONFIGURATION_BY_EQUIPMENT_MODEL,
  LIST_USERS_FILTERS,
  LIST_USERS_DISPLAYS,
} from "../../../../gql/bookingapi/queries";
import {
  CREATE_BOOKING_GROUP_CONFIGURATION,
  UPDATE_BOOKING_GROUP_CONFIGURATION,
  CREATE_BOOKING_GROUP_CIDM_CONFIGURATION,
  UPDATE_BOOKING_GROUP_CIDM_CONFIGURATION,
  DELETE_BOOKING_GROUP_CONFIGURATION,
} from "../../../../gql/bookingapi/mutations";
import { PaginationContext } from "../../../../components/shared/pagination/PaginationContext";
import { OwcPagination, OwcProgressSpinner } from "@one/react";
import BackdropPortal from "../../../../components/shared/BackdropPortal";
import { GroupContext } from "./context";
import { GroupTableMeta } from "./GroupMeta";
import TopBar from "./TopBar";
import { Formik } from "formik";
import { groupValidationSchema } from "./validationSchema";
import { sortBy } from "lodash";
import CustomList from "../../../../components/shared/List";
import { GroupPaginationStyled } from "../assign-group/GroupMainPageStyle";
import { DURATION } from "../../../../constants";
import { snackbarService } from "@one/web-components/dist/index.cjs.js";
import { BookingAccessControlContext } from "../BookingAccessControlContext";
import DeleteGroupDialog from "./DeleteGroupDialog";

const buildColumnSetRef = (ownerId, columnSetId) => {
  if (!ownerId || !columnSetId) {
    return null;
  }

  return `${ownerId}::${columnSetId}`;
};

const getColumnSetIdFromDisplay = (display) => {
  if (!display?.displayName) {
    return null;
  }

  return `EQUIPMENTLIST#${display.displayName}`;
};

const getDisplayNameFromColumnSetId = (columnSetId) => {
  if (!columnSetId || typeof columnSetId !== "string") {
    return "";
  }

  const [, displayName = ""] = columnSetId.split("#");
  return displayName;
};

const GroupContent = ({ client, user, siteName }) => {
  const { dispatchAction, fetching, limit, orderBy, sortDirection, nextData, refreshData } =
    useContext(PaginationContext);
  const { triggerRefresh } = useContext(BookingAccessControlContext);
  const {
    dialogOpen,
    setDialogOpen,
    dialogDeleteOpen,
    setDialogDeleteOpen,
    isEditMode,
    setIsEditMode,
    isDeleteMode,
    setIsDeleteMode,
    lastEditGroup,
    setLastEditGroup,
    userList,
    setUserList,
    registerDeleteHandler,
  } = useContext(GroupContext);
  const [groupList, setGroupList] = useState([]);
  const [searchGroup, setSearchGroup] = useState("");
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [showPerPage, setShowPerPage] = useState(5);
  const [isCheckingDelete, setIsCheckingDelete] = useState(false);
  const [deleteUsageInfo, setDeleteUsageInfo] = useState({
    ruleCount: 0,
    viewCount: 0,
    equipmentRows: [],
    modelLocationRows: [],
    viewRows: [],
  });
  const prevSearchGroupRef = useRef("");
  const checkUnicityForReason = async (groupName) => {
    let variables = { filter: groupName };
    const { items } = await getAllData({
      client,
      query: LIST_BOOKING_GROUP_CONFIGURATION_UNICITY_CHECK,
      variables,
      fetchPolicy: "network-only",
      dataPath: ["data", "listBookingGroupConfigurationByCreatedDate"],
      drillData: true,
    });
    return items.length > 0 ? false : true;
  };
  const getGroupList = async () => {
    let variables = {};
    const siteFilter = siteName
      ? { or: [{ siteName: { eq: siteName } }, { siteName: { attributeExists: false } }] }
      : null;
    if (searchGroup !== "") {
      variables = {
        ...variables,
        filter: siteFilter
          ? { and: [{ groupName: { contains: searchGroup } }, siteFilter] }
          : { groupName: { contains: searchGroup } },
      };
    } else if (siteFilter) {
      variables = { ...variables, filter: siteFilter };
    }
    const { items } = await getAllData({
      client,
      query: LIST_BOOKING_GROUP_CONFIGURATION_BY_CREATE_DATE,
      variables,
      fetchPolicy: "network-only",
      dataPath: ["data", "listBookingGroupConfigurationByCreatedDate"],
      drillData: false,
    });

    (items || []).sort((a, b) => (a.groupName || "").localeCompare(b.groupName || ""));

    setTotalCount(items.length);
    const lowIndex = pageNo === 1 ? 0 : (pageNo - 1) * showPerPage;
    const highIndex = pageNo === 1 ? showPerPage : lowIndex + showPerPage;
    const paginationData = items.slice(lowIndex, highIndex);
    setGroupList(paginationData);
  };

  const getUserList = async () => {
    const { items } = await getAllData({
      client,
      query: LIST_DIGITAL_LAB_BOOKING_USERS,
      fetchPolicy: "network-only",
      dataPath: ["data", "listDigitalLabBookingUsers"],
      drillData: true,
      variables: {
        limit: 1000,
      },
    });
    // Create a map to track used values and ensure uniqueness
    const usedValues = new Map();
    let list = items.map((item, index) => {
      // Generate display value
      const displayValue =
        item?.givenName && item?.familyName && item?.name
          ? `${item?.givenName} ${item?.familyName} - ${item?.name}`
          : item?.givenName && item?.familyName
            ? `${item?.givenName} ${item?.familyName}`
            : item.email;

      // Create a unique key using userId if available, or generate a unique key by appending an index
      let uniqueKey = item.userId || item.email;

      // If this value has been used before, make it unique by adding an index
      if (usedValues.has(displayValue)) {
        uniqueKey = `${uniqueKey}-${index}`;
      }

      // Track this value as used
      usedValues.set(displayValue, true);

      return {
        ...item,
        key: uniqueKey,
        value: displayValue,
      };
    });

    list = sortBy(list, "value");
    setUserList(list);
  };

  const chooseMutation = (isEditMode, isCIDM) => {
    if (isCIDM) {
      return isEditMode ? UPDATE_BOOKING_GROUP_CIDM_CONFIGURATION : CREATE_BOOKING_GROUP_CIDM_CONFIGURATION;
    } else {
      return isEditMode ? UPDATE_BOOKING_GROUP_CONFIGURATION : CREATE_BOOKING_GROUP_CONFIGURATION;
    }
  };

  const submit = async (data) => {
    setLoading(true);
    let checkUnicityStatus = await checkUnicityForReason(data?.groupName?.trim());
    if (checkUnicityStatus || isEditMode) {
      const list = (data?.users || []).map(({ email, familyName, givenName, name, userId }) => ({
        email,
        familyName,
        givenName,
        name,
        userId,
      }));
      let row = {
        groupName: data.groupName.trim(),
        users: list,
        siteName: siteName,
      };
      if (data?.useCIDMMembership) {
        row = {
          ...row,
          cidmGroup: {
            groupName: data.groupNameCIDM,
            cidmUrl: data.cidmRequestUrl,
            groupId: data.groupId,
            moreInfoUrl: data.moreInfoUrl,
          },
        };
      }
      try {
        await client.mutate({
          mutation: chooseMutation(isEditMode, data?.useCIDMMembership),
          variables: row,
          fetchPolicy: "no-cache",
        });
        dispatchAction({
          type: "clearAll",
        });
        setDialogOpen(false);
        snackbarService.show({
          message: isEditMode ? `Group updated successfully! ` : `Group created successfully! `,
          type: "success",
          duration: DURATION,
        });
      } catch (error) {
        setDialogOpen(false);
        snackbarService.show({
          message: isEditMode ? `Group updated failed!` : `Group created failed! `,
          type: "error",
          duration: DURATION,
        });
      } finally {
        triggerRefresh();
        setLoading(false);
      }
    } else {
      setDialogOpen(true);
      setLoading(false);
      snackbarService.show({
        message: `Group already exist`,
        type: "error",
        duration: DURATION,
      });
    }
  };

  useEffect(() => {
    getGroupList();
    getUserList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (searchGroup !== prevSearchGroupRef.current) {
      if (searchGroup) {
        setPageNo(1);
      }
      prevSearchGroupRef.current = searchGroup;
    }
    getGroupList();
    getUserList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchGroup, orderBy, sortDirection, limit, nextData, refreshData, pageNo, showPerPage]);

  const closeDialog = () => {
    setDialogOpen(false);
    setDialogDeleteOpen(false);
    setIsDeleteMode(false);
  };

  const getGroupUsageDetails = async (groupName) => {
    try {
      const [equipResult, modelResult, filtersResult, displaysResult] = await Promise.all([
        getAllData({
          client,
          query: LIST_BOOKING_ACCESS_CONFIGURATION_BY_EQUIPMENT,
          variables: { modelName: "BOOKINGACCESSCONFIG", filter: { groupName: { eq: groupName } } },
          fetchPolicy: "network-only",
          dataPath: ["data", "listBookingAccessConfigurationByEquipment"],
          drillData: true,
        }),
        getAllData({
          client,
          query: LIST_BOOKING_ACCESS_CONFIGURATION_BY_EQUIPMENT_MODEL,
          variables: { modelName: "BOOKINGACCESSCONFIG", filter: { groupName: { eq: groupName } } },
          fetchPolicy: "network-only",
          dataPath: ["data", "listBookingAccessConfigurationByEquipmentModel"],
          drillData: true,
        }),
        client.query({ query: LIST_USERS_FILTERS, variables: { siteName: user?.site }, fetchPolicy: "network-only" }),
        client.query({ query: LIST_USERS_DISPLAYS, variables: { siteName: user?.site }, fetchPolicy: "network-only" }),
      ]);

      const equipmentItems = equipResult.items || [];
      const modelItems = modelResult.items || [];
      const allFilters = filtersResult?.data?.listAllUsersFilters ?? [];
      const allDisplays = displaysResult?.data?.listAllUsersDisplays ?? [];
      const filterItems = allFilters.filter((f) => (f.sharedWithGroups || []).includes(groupName));
      const displayByReference = new Map(
        allDisplays
          .map((display) => {
            const columnSetId = getColumnSetIdFromDisplay(display);
            const ref = buildColumnSetRef(display?.userID, columnSetId);
            return [ref, display];
          })
          .filter(([ref]) => Boolean(ref))
      );

      const viewRows = [];
      const referencedDisplayIds = new Set();
      const equipmentRows = equipmentItems.map((item) => ({
        id: `equipment-${item.id}`,
        equipmentName: item.equipment?.shortIdentifier || item.equipment?.equipmentNickName || "-",
        membershipType: item.groups?.items?.[0]?.groupType || "-",
        cidmGroup: item.groups?.items?.[0]?.cidmGroup?.groupName || "-",
        users: (item.groups?.items?.[0]?.users || [])
          .map((u) => (u?.givenName && u?.familyName ? `${u.givenName} ${u.familyName}` : u?.email || ""))
          .filter(Boolean)
          .join(", ") || "-",
        bookingGroup: item.groupName || "-",
      }));

      const modelLocationRows = modelItems.map((item) => ({
        id: `model-${item.id}`,
        equipmentModel: item.equipmentModelName || item.equipmentModelId || "-",
        location: item.locationKey || "-",
        membershipType: item.groups?.items?.[0]?.groupType || "-",
        cidmGroup: item.groups?.items?.[0]?.cidmGroup?.groupName || "-",
        users: (item.groups?.items?.[0]?.users || [])
          .map((u) => (u?.givenName && u?.familyName ? `${u.givenName} ${u.familyName}` : u?.email || ""))
          .filter(Boolean)
          .join(", ") || "-",
        bookingGroup: item.groupName || "-",
      }));

      filterItems.forEach((filterItem) => {
        const displayReference = buildColumnSetRef(filterItem.columnSetUserID, filterItem.columnSetId);
        const displayItem = displayReference ? displayByReference.get(displayReference) : null;
        const fallbackDisplayName = getDisplayNameFromColumnSetId(filterItem.columnSetId);
        if (displayItem?.id) {
          referencedDisplayIds.add(displayItem.id);
        }

        viewRows.push({
          id: `filter-${filterItem.id}`,
          assignedGroups: (filterItem.sharedWithGroups || []).join(", ") || "-",
          filterName: filterItem.filterName || "-",
          columnSet: displayItem?.displayName || fallbackDisplayName || "-",
          createdBy: filterItem.createdBy || "-",
          modifiedBy: filterItem.modifiedBy || "-",
        });
      });

      const displayItems = allDisplays.filter((display) => referencedDisplayIds.has(display.id));

      return { equipmentItems, modelItems, filterItems, displayItems, equipmentRows, modelLocationRows, viewRows };
    } catch (error) {
      return {
        equipmentItems: [],
        modelItems: [],
        filterItems: [],
        displayItems: [],
        equipmentRows: [],
        modelLocationRows: [],
        viewRows: [],
      };
    }
  };

  const handleDeleteIconRequest = useCallback(async (item) => {
    setIsEditMode(false);
    setLastEditGroup(item);
    setIsCheckingDelete(true);
    const {
      equipmentItems,
      modelItems,
      filterItems,
      displayItems,
      equipmentRows,
      modelLocationRows,
      viewRows,
    } = await getGroupUsageDetails(item.groupName);
    setIsCheckingDelete(false);
    setDeleteUsageInfo({
      ruleCount: equipmentItems.length + modelItems.length,
      viewCount: viewRows.length,
      equipmentRows,
      modelLocationRows,
      viewRows,
      filterCount: filterItems.length,
      displayCount: displayItems.length,
    });
    setIsDeleteMode(true);
    setDialogDeleteOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    registerDeleteHandler(handleDeleteIconRequest);
  }, [registerDeleteHandler, handleDeleteIconRequest]);

  const deleteGroup = async () => {
    if (!lastEditGroup?.groupName) {
      return;
    }

    setLoading(true);
    try {
      await client.mutate({
        mutation: DELETE_BOOKING_GROUP_CONFIGURATION,
        variables: {
          modelName: "BOOKINGGROUPCONFIG",
          groupName: lastEditGroup.groupName,
        },
        fetchPolicy: "no-cache",
      });
      dispatchAction({
        type: "clearAll",
      });
      snackbarService.show({
        message: "Group deleted successfully!",
        type: "success",
        duration: DURATION,
      });
    } catch (error) {
      snackbarService.show({
        message: "Failed to delete group!",
        type: "error",
        duration: DURATION,
      });
    } finally {
      setLoading(false);
      setIsDeleteMode(false);
      setDialogDeleteOpen(false);
      triggerRefresh();
    }
  };

  return (
    <>
      <Formik
        validationSchema={groupValidationSchema}
        initialValues={{
          groupName: "",
          users: null,
          useCIDMMembership: false,
          groupNameCIDM: "",
          cidmRequestUrl: "",
          groupId: "",
          moreInfoUrl: "",
        }}
        isInitialValid={false}
        validateOnMount={false}
      >
        <>
          <GroupDialog
            open={dialogOpen}
            onCancel={closeDialog}
            isEditMode={isEditMode}
            submit={submit}
            userList={userList}
            loading={loading}
          />
          {isDeleteMode && (
            <DeleteGroupDialog
              open={dialogDeleteOpen}
              onCancel={closeDialog}
              deleteGroup={deleteGroup}
              loading={loading}
              usageInfo={deleteUsageInfo}
              bookingGroup={lastEditGroup?.groupName}
            />
          )}
          <BackdropPortal show={isCheckingDelete} />

          <TopBar setDialogOpen={setDialogOpen} setIsEditMode={setIsEditMode} setSearchGroup={setSearchGroup} />

          <CustomList
            meta={GroupTableMeta}
            data={groupList}
            fieldArray={[]}
            onRequestSort={false}
            isReverseOrder={true}
            orderBy={"name"}
            loading={fetching}
          />
        </>
      </Formik>
      <GroupPaginationStyled style={{ gridTemplateColumns: loading ? "auto 1fr" : "1fr" }}>
        {loading && <OwcProgressSpinner />}
        <OwcPagination
          onPageChange={(e) => {
            setShowPerPage(e.detail.rowsPerPage);
            setPageNo(e.detail.page);
          }}
          options={[5, 10, 20, 50]}
          rowsPerPage={showPerPage}
          total={Math.ceil(totalCount / showPerPage)}
          page={pageNo}
        >
          <span slot="rows-per-page">Rows per page</span>
          <div slot="indicator">
            {pageNo} of {Math.ceil(totalCount / showPerPage)}
          </div>
        </OwcPagination>
      </GroupPaginationStyled>
    </>
  );
};
const mapStateToProps = (state) => ({
  user: state.user,
  siteName: state.user.site,
});

export default compose(connect(mapStateToProps), withApollo)(GroupContent);
